import axios from "axios";
import { APIClient } from "./api_helper";
import * as url from "./url_helper";

const api = new APIClient();

export const postLogin = (data) => api.create(url.POST_KRONALY_LOGIN, data);
export const postRefreshToken = (data) => api.create(url.POST_KRONALY_REFRESH, data);
export const postLogout = (data) => api.create(url.POST_KRONALY_LOGOUT, data);
export const getMe = () => api.get(url.GET_ME);
export const getWorkspaces = () => api.get(url.GET_WORKSPACES);

// Avatar del user (global). Multipart para el upload.
export const setMyAvatar = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(url.ME_AVATAR, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const removeMyAvatar = () => api.delete(url.ME_AVATAR);

// Avatar del empleado (workspace-scoped).
export const setEmployeeAvatar = (employeeProfileId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(url.employeeAvatarPath(employeeProfileId), fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const removeEmployeeAvatar = (employeeProfileId) =>
  api.delete(url.employeeAvatarPath(employeeProfileId));

// Workspace modules (capa 1 del modelo de permisos)
export const getWorkspaceModules = () => api.get(url.GET_WORKSPACE_MODULES);
export const setWorkspaceModuleEnabled = (moduleKey, enabled) =>
  api.update(url.workspaceModulePath(moduleKey), { enabled });

// Workspace access (membresías: invitar, cambiar rol, revocar)
export const listWorkspaceAccess = () => api.get(url.GET_WORKSPACE_ACCESS);
export const inviteToWorkspace = (email, role) =>
  api.create(url.POST_WORKSPACE_ACCESS, { email, role });
export const changeMembershipRole = (membershipId, role) =>
  api.update(url.workspaceAccessRolePath(membershipId), { role });
export const revokeWorkspaceAccess = (membershipId) =>
  api.delete(url.workspaceAccessPath(membershipId));

// Membership capabilities
export const listMembershipCapabilities = (membershipId) =>
  api.get(url.membershipCapabilitiesPath(membershipId));
export const setMembershipCapability = (membershipId, capability, granted) =>
  api.put(url.membershipCapabilityPath(membershipId, capability), { granted });
export const clearMembershipCapability = (membershipId, capability) =>
  api.delete(url.membershipCapabilityPath(membershipId, capability));

// Inspection tokens — Acceso remoto regulatorio (Inspección de Trabajo).
// Solo owners pueden listar/emitir/revocar. El plain text se devuelve
// SOLO una vez en createInspectionToken.
export const listInspectionTokens = () =>
  api.get(url.GET_INSPECTION_TOKENS);
export const createInspectionToken = (data) =>
  api.create(url.POST_INSPECTION_TOKENS, data);
export const revokeInspectionToken = (tokenId) =>
  api.create(url.inspectionTokenRevokePath(tokenId), {});

// API keys de Integración — API pública para apps externas (cuadros de mando,
// ETL…). Solo owner/admin. El plain text se devuelve SOLO una vez en createApiKey.
export const listApiKeys = () =>
  api.get(url.GET_API_KEYS);
export const createApiKey = (data) =>
  api.create(url.POST_API_KEYS, data);
export const revokeApiKey = (apiKeyId) =>
  api.create(url.apiKeyRevokePath(apiKeyId), {});

// Workspace invitations
export const getInvitationByToken = (token) =>
  api.get(url.invitationByTokenPath(token));
export const acceptInvitation = (token) =>
  api.create(url.acceptInvitationPath(token), {});
export const activateInvitation = (token, password) =>
  api.create(url.activateInvitationPath(token), { password });
export const resendInvitation = (invitationId) =>
  api.create(url.resendInvitationPath(invitationId), {});
export const revokeInvitation = (invitationId) =>
  api.delete(url.revokeInvitationPath(invitationId));

// Employees
export const getEmployees = (params) => api.get(url.GET_EMPLOYEES, params);
export const createEmployee = (data) => api.create(url.GET_EMPLOYEES, data);
export const getEmployee = (id) => api.get(url.employeePath(id));
export const inviteEmployee = (id) => api.create(url.employeeInvitePath(id), {});
export const updateEmployee = (id, data) => api.update(url.employeePath(id), data);
export const getEmployeePersonalDetails = (id) =>
  api.get(url.employeePersonalDetailsPath(id));
export const upsertEmployeePersonalDetails = (id, data) =>
  api.update(url.employeePersonalDetailsPath(id), data);

// Time Tracking
export const getWorkspaceTodayStatus = () => api.get(url.GET_TT_TODAY);
export const getJornadaLaboral = (params) => api.get(url.GET_TT_JORNADA, params);

/**
 * Log inmutable de cambios en fichaje (Fase 2 normativa).
 * Accesible para managers (employees:manage) y RLT (time_tracking:rlt_view).
 * params: { start_date: 'YYYY-MM-DD', end_date: 'YYYY-MM-DD' }
 */
export const listTimeTrackingAudits = (params) =>
  api.get(url.GET_TT_AUDITS, params);

/**
 * Verifica integridad de la hash chain del audit log del workspace activo.
 * Devuelve {checked, valid, first_mismatch_id}.
 */
export const verifyAuditChain = () =>
  api.get(url.GET_TT_AUDIT_CHAIN_VERIFY);

/**
 * Solicita una corrección de jornada (fichaje digital).
 *
 * @param {string} date YYYY-MM-DD
 * @param {{
 *   segments: Array<{ clock_in_at: string, clock_out_at: ?string }>,
 *   breaks: Array<{ start_at: string, end_at: string }>,
 *   note?: ?string,
 *   employee_profile_id?: ?string,
 * }} payload
 */
export const requestWorkdayCorrection = (date, payload) =>
  api.create(url.ttWorkdayCorrectionPath(date), payload);

/**
 * @param {{ scope?: 'mine'|'to-approve'|'all', status?: string }} params
 */
export const listWorkdayCorrections = (params = {}) =>
  api.get(url.TT_CORRECTIONS, params);

/** @param {string} id */
export const getWorkdayCorrection = (id) => api.get(url.ttCorrectionPath(id));

export const approveWorkdayCorrection = (id, comment) =>
  api.create(url.ttCorrectionApprovePath(id), comment != null ? { comment } : {});

export const rejectWorkdayCorrection = (id, comment) =>
  api.create(url.ttCorrectionRejectPath(id), comment != null ? { comment } : {});

export const cancelWorkdayCorrection = (id) =>
  api.create(url.ttCorrectionCancelPath(id), {});

// Jornada especial (intensivas, etc.)
/** @param {{ scope?: 'mine'|'pending'|'workspace', status?: string }} params */
export const listSpecialSchedules = (params = {}) =>
  api.get(url.TT_SPECIAL_SCHEDULES, params);
export const createSpecialSchedule = (data) => api.create(url.TT_SPECIAL_SCHEDULES, data);
/** @param {string} id */
export const getSpecialSchedule = (id) => api.get(url.ttSpecialSchedulePath(id));
export const approveSpecialSchedule = (id, comment) =>
  api.create(url.ttSpecialScheduleApprovePath(id), comment != null ? { comment } : {});
export const rejectSpecialSchedule = (id, comment) =>
  api.create(url.ttSpecialScheduleRejectPath(id), comment != null ? { comment } : {});
export const cancelSpecialSchedule = (id) =>
  api.create(url.ttSpecialScheduleCancelPath(id), {});
/** @param {string} id */
export const deleteSpecialSchedule = (id) => api.delete(url.ttSpecialSchedulePath(id));

export const getTimeTrackingSettings = () => api.get(url.TT_SETTINGS);
/** @param {{ correction_mode?: 'free'|'requires_approval', close_grace_days?: number }} payload */
export const updateTimeTrackingSettings = (payload) =>
  api.put(url.TT_SETTINGS, payload);

export const listMonthClosures = (params = {}) => api.get(url.TT_MONTHS, params);
export const closeMonth = (yearMonth) => api.create(url.ttMonthClosePath(yearMonth), {});
export const reopenMonth = (yearMonth) => api.delete(url.ttMonthClosePath(yearMonth));

/**
 * Encola un reporte para generación en background.
 * @param {{ type: 'hours'|'absences', format: 'pdf'|'csv', params: object }} payload
 */
export const enqueueReport = (payload) => api.create(url.REPORT_JOBS, payload);

/** @param {{ status?: string, limit?: number }} params */
export const listReportJobs = (params = {}) => api.get(url.REPORT_JOBS, params);

/**
 * Descarga un reporte generado. Usa la instancia singleton de axios (la
 * misma que `APIClient`) para que viaje el `baseURL`, el Bearer token y
 * el workspace header configurados globalmente, y luego dispara una
 * descarga programática del blob.
 */
export const downloadReportJob = async (id, fallbackName = "reporte") => {
  const res = await axios.get(url.reportJobDownloadPath(id), { responseType: "blob" });
  // axios interceptor unwraps response.data, so `res` is already the Blob
  const blob = res instanceof Blob ? res : res.data;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
};

/**
 * Reporte agregado de horas trabajadas + horas extra.
 *
 * @param {{ start_date: string, end_date: string, employee_profile_ids?: string[] }} params
 */
export const getHoursReport = (params) => api.get(url.TT_REPORT_HOURS, params);

/**
 * Reporte agregado de uso de ausencias por empleado y tipo.
 *
 * @param {{ start_date: string, end_date: string, employee_profile_ids?: string[], include_balance?: boolean }} params
 */
export const getAbsencesUsageReport = (params) =>
  api.get(url.ABSENCES_REPORT_USAGE, params);

// Contracts
export const getEmployeeContracts = (employeeProfileId) =>
  api.get(url.employeeContractsPath(employeeProfileId));
export const getEmployeeActiveContract = (employeeProfileId, params) =>
  api.get(url.employeeActiveContractPath(employeeProfileId), params);
export const createContract = (data) => api.create(url.POST_CONTRACTS, data);
export const updateContract = (contractId, data) =>
  api.update(url.contractPath(contractId), data);
export const terminateContract = (contractId, data) =>
  api.create(url.contractTerminatePath(contractId), data);
export const getContractTerms = (contractId) =>
  api.get(url.contractTermsPath(contractId));
export const addContractTerms = (contractId, data) =>
  api.create(url.contractTermsPath(contractId), data);

// Catálogos (work-schedules, holiday-groups, vacation-policies)
export const getWorkSchedules = () => api.get(url.WORK_SCHEDULES);
export const createWorkSchedule = (data) => api.create(url.WORK_SCHEDULES, data);
export const updateWorkSchedule = (id, data) =>
  api.update(`${url.WORK_SCHEDULES}/${id}`, data);
export const getHolidayGroups = () => api.get(url.HOLIDAY_GROUPS);
export const createHolidayGroup = (data) => api.create(url.HOLIDAY_GROUPS, data);
export const updateHolidayGroup = (id, data) => api.update(url.holidayGroupPath(id), data);
export const deleteHolidayGroup = (id) => api.delete(url.holidayGroupPath(id));
export const getHolidaysByGroup = (id) => api.get(url.holidayGroupHolidaysPath(id));
export const createHoliday = (groupId, data) =>
  api.create(url.holidayGroupHolidaysPath(groupId), data);
export const deleteHoliday = (id) => api.delete(url.holidayPath(id));
export const getVacationPolicies = () => api.get(url.VACATION_POLICIES);
export const getVacationPolicy = (id) => api.get(url.vacationPolicyPath(id));
export const createVacationPolicy = (data) =>
  api.create(url.VACATION_POLICIES, data);
export const updateVacationPolicy = (id, data) =>
  api.update(url.vacationPolicyPath(id), data);
export const deleteVacationPolicy = (id) =>
  api.delete(url.vacationPolicyPath(id));

// Notifications
export const getNotifications = (params) => api.get(url.NOTIFICATIONS, params);
export const getNotificationsUnreadCount = () => api.get(url.NOTIFICATIONS_UNREAD_COUNT);
export const markNotificationRead = (id) => api.create(url.notificationReadPath(id), {});
export const markAllNotificationsRead = () => api.create(url.NOTIFICATIONS_READ_ALL, {});
export const deleteNotification = (id) => api.delete(url.notificationPath(id));

// Notification preferences
export const getNotificationPreferences = () => api.get(url.GET_NOTIFICATION_PREFERENCES);
export const setNotificationPreference = (type, inApp, email) =>
  api.put(url.notificationPreferencePath(type), { in_app: inApp, email });
export const resetNotificationPreference = (type) =>
  api.delete(url.notificationPreferencePath(type));

// Absences
export const getAbsences = (params) => api.get(url.ABSENCES, params);
export const createAbsence = (data) => api.create(url.ABSENCES, data);
export const approveAbsence = (id, data = {}) => api.create(url.absenceApprovePath(id), data);
export const rejectAbsence = (id, data = {}) => api.create(url.absenceRejectPath(id), data);
export const cancelAbsence = (id) => api.create(url.absenceCancelPath(id), {});
export const getEmployeeAbsenceBalance = (employeeProfileId) =>
  api.get(url.employeeAbsenceBalancePath(employeeProfileId));
export const getEmployeeAbsenceAdjustments = (employeeProfileId) =>
  api.get(url.employeeAbsenceAdjustmentsPath(employeeProfileId));
export const grantEmployeeAbsenceAdjustment = (employeeProfileId, data) =>
  api.create(url.employeeAbsenceAdjustmentsPath(employeeProfileId), data);
export const deleteEmployeeAbsenceAdjustment = (employeeProfileId, adjustmentId) =>
  api.delete(url.employeeAbsenceAdjustmentPath(employeeProfileId, adjustmentId));

// Overtime
export const getOvertimeRequests = (params) =>
  api.get(url.OVERTIME_REQUESTS, params);
export const getMyOvertimeRequests = (params) =>
  api.get(url.OVERTIME_REQUESTS_MY, params);
export const createOvertimeRequest = (data) =>
  api.create(url.OVERTIME_REQUESTS, data);
export const cancelOvertimeRequest = (id) =>
  api.create(url.overtimeCancelPath(id), {});
export const decideOvertimeRequest = (id, data) =>
  api.create(url.overtimeDecidePath(id), data);
export const getOvertimeAllowances = (employeeProfileId) =>
  api.get(url.OVERTIME_ALLOWANCES, { employee_profile_id: employeeProfileId });
export const createOvertimeAllowance = (data) =>
  api.create(url.OVERTIME_ALLOWANCES, data);
export const deactivateOvertimeAllowance = (id) =>
  api.update(url.overtimeAllowanceDeactivatePath(id), {});

// Workday incidents
export const getWorkdayIncidents = (params) =>
  api.get(url.WORKDAY_INCIDENTS, params);
export const reportWorkdayIncident = (data) =>
  api.create(url.WORKDAY_INCIDENTS, data);
export const validateWorkdayIncident = (id, data) =>
  api.create(url.workdayIncidentValidatePath(id), data);

// Excess workdays
export const getExcessWorkdays = (params) =>
  api.get(url.EXCESS_WORKDAYS, params);

// Teams
export const getTeams = () => api.get(url.TEAMS);
export const getTeam = (id) => api.get(url.teamPath(id));
export const createTeam = (data) => api.create(url.TEAMS, data);
export const updateTeam = (id, data) => api.update(url.teamPath(id), data);
export const deleteTeam = (id) => api.delete(url.teamPath(id));
export const addTeamMembers = (id, employeeProfileIds) =>
  api.create(url.teamMembersPath(id), { employee_profile_ids: employeeProfileIds });
export const removeTeamMember = (teamId, employeeProfileId) =>
  api.delete(url.teamMemberPath(teamId, employeeProfileId));

// Document requests
export const getDocumentRequests = (params) => api.get(url.DOCUMENT_REQUESTS, params);
export const createDocumentRequest = (data) => api.create(url.DOCUMENT_REQUESTS, data);
/**
 * Crea N solicitudes en una sola llamada — una por cada empleado.
 *
 * @param {{ employee_profile_ids: string[], category_id: string, title: string, message?: string }} data
 */
export const bulkCreateDocumentRequests = (data) =>
  api.create(url.DOCUMENT_REQUESTS_BULK, data);
export const cancelDocumentRequest = (id) =>
  api.create(url.documentRequestCancelPath(id), {});

/**
 * Cumple una solicitud subiendo un archivo (multipart).
 */
export const fulfillDocumentRequest = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(url.documentRequestFulfillPath(id), fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Document categories
export const getDocumentCategories = () => api.get(url.DOCUMENT_CATEGORIES);
export const createDocumentCategory = (data) =>
  api.create(url.DOCUMENT_CATEGORIES, data);
export const updateDocumentCategory = (id, data) =>
  api.update(url.documentCategoryPath(id), data);
export const deleteDocumentCategory = (id) =>
  api.delete(url.documentCategoryPath(id));

// Documents
export const getDocuments = (params) => api.get(url.DOCUMENTS, params);
export const getDocument = (id) => api.get(url.documentPath(id));
export const getDocumentEvents = (id) => api.get(url.documentEventsPath(id));
export const publishDocument = (id) => api.create(url.documentPublishPath(id), {});
export const archiveDocument = (id) => api.create(url.documentArchivePath(id), {});
export const unarchiveDocument = (id) => api.create(url.documentUnarchivePath(id), {});
export const deleteDocument = (id) => api.delete(url.documentPath(id));
export const updateDocumentMetadata = (id, data) => api.update(url.documentPath(id), data);

/**
 * MIME types que el backend permite servir inline (PreviewDocumentUseCase).
 * Replicado en el front para decidir si pedimos preview o solo descarga.
 */
export const PREVIEWABLE_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

/**
 * Pide el preview como blob y devuelve una object URL que el front puede
 * meter en un <iframe> o <img>. El llamante DEBE liberar la URL con
 * URL.revokeObjectURL cuando ya no la use.
 */
export const fetchDocumentPreviewBlobUrl = async (id) => {
  const response = await axios.get(url.documentPreviewPath(id), {
    responseType: "blob",
  });
  const blob = response instanceof Blob ? response : response?.data;
  if (!(blob instanceof Blob)) {
    throw new Error("Respuesta de preview inválida.");
  }
  return window.URL.createObjectURL(blob);
};

/**
 * Sube un documento como multipart/form-data. Usa axios directo en lugar de
 * APIClient porque éste fija `application/json` y FormData necesita boundary.
 *
 * @param {{
 *  title: string,
 *  description?: string,
 *  category_id: string,
 *  visibility: "workspace"|"employee"|"private",
 *  employee_profile_id?: string,
 *  publish?: boolean,
 *  expires_at?: string,
 *  file: File,
 * }} input
 */
export const uploadDocument = (input) => {
  const fd = new FormData();
  fd.append("title", input.title);
  if (input.description) fd.append("description", input.description);
  fd.append("category_id", input.category_id);
  fd.append("visibility", input.visibility);
  if (input.employee_profile_id) fd.append("employee_profile_id", input.employee_profile_id);
  if (input.publish) fd.append("publish", "1");
  if (input.expires_at) fd.append("expires_at", input.expires_at);
  fd.append("file", input.file);
  return axios.post(url.DOCUMENTS, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// -----------------------------------------------------------------------------
// Internal Documentation (jerarquía documental corporativa)
// -----------------------------------------------------------------------------

// Niveles jerárquicos (catálogo configurable por workspace)
export const getDocumentationLevels = () => api.get(url.DOCUMENTATION_LEVELS);
export const createDocumentationLevel = (data) =>
  api.create(url.DOCUMENTATION_LEVELS, data);
export const updateDocumentationLevel = (id, data) =>
  api.update(url.documentationLevelPath(id), data);
export const deleteDocumentationLevel = (id) =>
  api.delete(url.documentationLevelPath(id));

// Tipos documentales
export const getInternalDocumentTypes = () => api.get(url.DOCUMENTATION_TYPES);
export const createInternalDocumentType = (data) =>
  api.create(url.DOCUMENTATION_TYPES, data);
export const updateInternalDocumentType = (id, data) =>
  api.update(url.documentationTypePath(id), data);
export const deleteInternalDocumentType = (id) =>
  api.delete(url.documentationTypePath(id));

// Documentos internos
export const getInternalDocuments = (params) =>
  api.get(url.INTERNAL_DOCUMENTS, params);
export const getInternalDocument = (id) =>
  api.get(url.internalDocumentPath(id));
export const updateInternalDocumentMetadata = (id, data) =>
  api.update(url.internalDocumentPath(id), data);
export const publishInternalDocument = (id) =>
  api.create(url.internalDocumentPublishPath(id), {});
export const archiveInternalDocument = (id) =>
  api.create(url.internalDocumentArchivePath(id), {});
export const unarchiveInternalDocument = (id) =>
  api.create(url.internalDocumentUnarchivePath(id), {});
export const deleteInternalDocument = (id) =>
  api.delete(url.internalDocumentPath(id));

/**
 * Sube un documento interno. Multipart porque incluye el binario.
 *
 * @param {{
 *  code: string,
 *  title: string,
 *  description?: string,
 *  level_id: string,
 *  type_id?: string,
 *  parent_document_id?: string,
 *  author_employee_profile_id?: string,
 *  effective_at?: string,
 *  publish?: boolean,
 *  file: File,
 * }} input
 */
export const uploadInternalDocument = (input) => {
  const fd = new FormData();
  fd.append("code", input.code);
  fd.append("title", input.title);
  if (input.description) fd.append("description", input.description);
  fd.append("level_id", input.level_id);
  if (input.type_id) fd.append("type_id", input.type_id);
  if (input.parent_document_id) fd.append("parent_document_id", input.parent_document_id);
  if (input.author_employee_profile_id) fd.append("author_employee_profile_id", input.author_employee_profile_id);
  if (input.effective_at) fd.append("effective_at", input.effective_at);
  if (input.publish) fd.append("publish", "1");
  if (input.requires_acknowledgement) fd.append("requires_acknowledgement", "1");
  fd.append("file", input.file);
  return axios.post(url.INTERNAL_DOCUMENTS, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Acuses de lectura de documentación interna
export const acknowledgeInternalDocument = (id) =>
  api.create(url.internalDocAcknowledgePath(id), {});
export const getInternalDocumentAcknowledgements = (id) =>
  api.get(url.internalDocAcknowledgementsPath(id));

/**
 * Sube una nueva versión de un documento existente. Multipart por el binario.
 *
 * @param {{ id: string, file: File, version_notes?: string, effective_at?: string }} input
 */
export const uploadInternalDocumentVersion = (input) => {
  const fd = new FormData();
  fd.append("file", input.file);
  if (input.version_notes) fd.append("version_notes", input.version_notes);
  if (input.effective_at) fd.append("effective_at", input.effective_at);
  return axios.post(url.internalDocVersionsPath(input.id), fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const fetchInternalDocumentPreviewBlobUrl = async (id) => {
  const response = await axios.get(url.internalDocumentPreviewPath(id), {
    responseType: "blob",
  });
  const blob = response instanceof Blob ? response : response?.data;
  if (!(blob instanceof Blob)) {
    throw new Error("Respuesta de preview inválida.");
  }
  return window.URL.createObjectURL(blob);
};

export const downloadInternalDocument = async (id, filename = "documento") => {
  const blob = await axios.get(url.internalDocumentDownloadPath(id), {
    responseType: "blob",
  });
  const actualBlob = blob instanceof Blob ? blob : blob?.data;
  if (!(actualBlob instanceof Blob)) {
    throw new Error("Respuesta de descarga inválida.");
  }
  const blobUrl = window.URL.createObjectURL(actualBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Descarga un documento como blob y dispara la descarga en el navegador.
 * El backend ya devuelve Content-Disposition pero el interceptor de axios
 * desempaqueta a `response.data`, por lo que pasamos el filename del metadata.
 */
export const downloadDocument = async (id, filename = "documento") => {
  const blob = await axios.get(url.documentDownloadPath(id), {
    responseType: "blob",
  });
  // El interceptor devuelve response.data directamente, que con responseType
  // 'blob' es ya el Blob.
  const actualBlob = blob instanceof Blob ? blob : blob?.data;
  if (!(actualBlob instanceof Blob)) {
    throw new Error("Respuesta de descarga inválida.");
  }
  const blobUrl = window.URL.createObjectURL(actualBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// ── Soporte IT (Help Desk) ─────────────────────────────────────────
export const getSupportMeta = () => api.get(url.SUPPORT_META);
export const getSupportTickets = (params) => api.get(url.SUPPORT_TICKETS, params);
export const createSupportTicket = (data) => api.create(url.SUPPORT_TICKETS, data);
export const getSupportTicket = (id) => api.get(url.supportTicketPath(id));
export const updateSupportTicket = (id, data) => api.update(url.supportTicketPath(id), data);
export const assignSupportTicket = (id, assigneeMembershipId) =>
  api.create(url.supportTicketAssignPath(id), { assignee_membership_id: assigneeMembershipId });
export const setSupportTicketStatus = (id, status) =>
  api.create(url.supportTicketStatusPath(id), { status });
export const addSupportTicketComment = (id, body, internal = false) =>
  api.create(url.supportTicketCommentsPath(id), { body, internal });
export const getSupportCategories = (params) => api.get(url.SUPPORT_CATEGORIES, params);
export const createSupportCategory = (data) => api.create(url.SUPPORT_CATEGORIES, data);
export const updateSupportCategory = (id, data) => api.update(url.supportCategoryPath(id), data);
export const deleteSupportCategory = (id) => api.delete(url.supportCategoryPath(id));

// Soporte IT · Inventario
export const getSupportAssets = (params) => api.get(url.SUPPORT_ASSETS, params);
export const createSupportAsset = (data) => api.create(url.SUPPORT_ASSETS, data);
export const getSupportAsset = (id) => api.get(url.supportAssetPath(id));
export const updateSupportAsset = (id, data) => api.update(url.supportAssetPath(id), data);
export const setSupportAssetStatus = (id, status) => api.create(url.supportAssetStatusPath(id), { status });
export const assignSupportAsset = (id, data) => api.create(url.supportAssetAssignPath(id), data);
export const returnSupportAsset = (id, data) => api.create(url.supportAssetReturnPath(id), data);
export const addSupportAssetMaintenance = (id, data) => api.create(url.supportAssetMaintenancesPath(id), data);
export const getSupportTechniciansConfig = () => api.get(url.SUPPORT_CONFIG_TECHNICIANS);
export const setSupportTechnicianConfig = (membershipId, data) => api.put(url.supportConfigTechnicianPath(membershipId), data);
export const getSupportAssetTypes = (params) => api.get(url.SUPPORT_ASSET_TYPES, params);
export const createSupportAssetType = (data) => api.create(url.SUPPORT_ASSET_TYPES, data);
export const updateSupportAssetType = (id, data) => api.update(url.supportAssetTypePath(id), data);
export const deleteSupportAssetType = (id) => api.delete(url.supportAssetTypePath(id));

// Recursos IT
export const getSupportResources = (params) => api.get(url.SUPPORT_RESOURCES, params);
export const createSupportResource = (data) => api.create(url.SUPPORT_RESOURCES, data);
export const getSupportResource = (id) => api.get(url.supportResourcePath(id));
export const updateSupportResource = (id, data) => api.update(url.supportResourcePath(id), data);
export const deleteSupportResource = (id) => api.delete(url.supportResourcePath(id));
export const setSupportResourceStatus = (id, status) => api.create(url.supportResourceStatusPath(id), { status });
export const grantSupportResourceAccess = (id, employeeProfileIds) =>
  api.create(url.supportResourceAccessPath(id), { employee_profile_ids: employeeProfileIds });
export const revokeSupportResourceAccess = (id, employeeProfileId) =>
  api.delete(url.supportResourceAccessMemberPath(id, employeeProfileId));
export const addSupportResourceDocument = (id, data) => api.create(url.supportResourceDocumentsPath(id), data);
export const removeSupportResourceDocument = (id, docId) => api.delete(url.supportResourceDocumentPath(id, docId));
